import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ModalContext from '../contexts/modal.js';
import useGlobal from '../hooks/useGlobal.js';
import useUser from '../hooks/useUser.js';
import { resolveUrns } from '../helpers/api/getPage.js';
import Modal from './Modal.js';
import RichText from './RichTextContent.js';

import '../styles/quickLinks.css';

const resolveIconPath = (icon) => icon.includes("/") ? icon : `${process.env.PUBLIC_URL}/assets/icons/links/${ icon }`;

const Card = ({
    heading = "Card title",
    note = "",
    icon = "",
    to = null,
    onClick = null
}) => {
    const parsedIcon = resolveIconPath(icon);

    const content = (
        <>
            { icon && (
                <div className="icon">
                    <img src={ parsedIcon } alt={ heading } />
                </div>
            )}
            <strong>{ heading }</strong>

            { note && (
                <p>{ note }</p>
            )}
        </>
    );

    if(to) {
        return <Link className="card" to={ to }>{ content }</Link>;
    }

    if(onClick) {
        return (
            <div
                className="card"
                role="button"
                tabIndex={ 0 }
                onClick={ onClick }
                onKeyDown={ event => (event.key === "Enter" || event.key === " ") && onClick(event) }
            >
                { content }
            </div>
        );
    }

    return <div className="card">{ content }</div>;
};

const cardPageIndex = card => {
    if(card?.linkType !== "page") {
        return null;
    }
    try {
        const [ pageIndex ] = JSON.parse(card?.linkPage || "[]");
        return typeof pageIndex === "number" ? pageIndex : null;
    }
    catch (error) {
        console.error(`Failed parsing Quick Links target page: ${error}`);
        return null;
    }
};

/* Resolves every card's configured link in one batched request. */
const resolveCardLinks = async ({ cards, logout }) => {
    const pageIndexes = cards.map(cardPageIndex);
    const uniqueIndexes = [
            ...new Set(pageIndexes.filter(
            index => index !== null
        ))
    ];
    const resolvedPages = uniqueIndexes.length ? await resolveUrns({
        indexes: uniqueIndexes,
        logout
    }) : [];
    const urnByIndex = new Map(resolvedPages.map(({ index, urn }) => [
        index, urn
    ]));

    return cards.map((card, i) => {
        const pageIndex = pageIndexes[i];

        if(pageIndex !== null) {
            const urn = urnByIndex.get(pageIndex);
            return urn ? {
                link: `/story/${ urn }`
            } : {};
        }

        if(card?.linkType === "richtext" && card?.content) {
            return { richText: card.content };
        }

        return {};
    });
};

const QuickLinks = () => {
    const { setModalActive } = useContext(ModalContext);
    const { getCategoryOption } = useGlobal();
    const { logout } = useUser();

    const [ cards, setCards ] = useState([]);
    const [ cardLinks, setCardLinks ] = useState([]);
    const [ activeModal, setActiveModal ] = useState(null);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            const config = await getCategoryOption({ category: "quickLinks" }) || {};
            const quickLinksCards = (() => {
                try {
                    return JSON.parse(config.quickLinksCards || "[]");
                }
                catch (error) {
                    console.error(`Failed parsing Quick Links cards: ${error}`);
                    return [];
                }
            })();
            if(!isMounted) {
                return;
            }
            setCards(quickLinksCards);

            const links = await resolveCardLinks({
                cards: quickLinksCards,
                logout
            });

            isMounted && setCardLinks(links);
        })();
        return () => {
            isMounted = false;
        };
    }, []);

    const openCardModal = (options) => {
        setActiveModal({
            ...options,
            ...(options.icon && {
                icon: resolveIconPath(options.icon)
            })
        });
        setModalActive(true);
    };

    return (
        <div className="quick-links">
            { cards.map((card, index) => (
                <Card
                    key = { index }
                    heading = { card.title }
                    note = { card.note }
                    icon = { card.icon }
                    to = { cardLinks[index]?.link }
                    onClick = {
                        cardLinks[index]?.richText ? () => {
                            openCardModal({
                                heading: card.title,
                                content: cardLinks[index].richText,
                                icon: card.icon
                            });
                        } : null
                    }
                />
            )) }

            <Modal
                fullScreen={ false }
                icon={ activeModal?.icon }
                title={ activeModal?.heading || "" }
                modalContent={
                    <RichText html={ activeModal?.content || "" } />
                }
            />
        </div>
    );
};

export default QuickLinks;
